import React, { useState } from 'react';
import { AlertTriangle, Loader2, ShieldCheck } from 'lucide-react';
import { Badge, Button, Checkbox } from '@roger/ui-components';
import { screenMedicationInteractions } from '../../services/api';
import type {
  DdiFinding,
  InteractionScreen,
  NotScreenedAllergy,
  NotScreenedMedication,
  ObsoleteMedication,
} from '../../services/api';

import {
  ALLERGY_BADGE_CLASSES,
  SEVERITY_BADGE_CLASSES,
  SEVERITY_LABELS,
  SEVERITY_ORDER,
} from './screeningStyles';

type ScreenStatus = 'idle' | 'loading' | 'ready' | 'error';

interface InteractionScreenPanelProps {
  patientId: string;
  acknowledged: boolean;
  onAcknowledgedChange: (value: boolean) => void;
  // Parent forces the unavailable state (e.g. after the sign call 422s because
  // no acknowledgment was supplied) so the checkbox appears without re-running.
  forceUnavailable?: boolean;
}

const severityBucket = (finding: DdiFinding): string => {
  const code = finding.severity ?? '9';
  return SEVERITY_LABELS[code] ? code : '9';
};

const NotScreenedList: React.FC<{
  medications: NotScreenedMedication[];
  allergies: NotScreenedAllergy[];
}> = ({ medications, allergies }) => {
  if (medications.length === 0 && allergies.length === 0) return null;
  return (
    <div className='space-y-1'>
      <p className='text-xs font-medium text-muted-foreground'>Not screened</p>
      <ul className='space-y-0.5 text-xs text-muted-foreground'>
        {medications.map(med => (
          <li key={med.patient_medication_id}>
            {med.display_name || 'Medication'} — {med.reason}
          </li>
        ))}
        {allergies.map(allergy => (
          <li key={allergy.patient_allergy_id}>
            {allergy.allergy_name || 'Allergy'} — {allergy.reason}
          </li>
        ))}
      </ul>
    </div>
  );
};

const ObsoleteMedicationsList: React.FC<{
  medications: ObsoleteMedication[];
}> = ({ medications }) => {
  if (medications.length === 0) return null;
  return (
    <div className='space-y-1'>
      <p className='text-xs font-medium text-muted-foreground'>
        Obsolete medications
      </p>
      <ul className='space-y-0.5 text-xs text-muted-foreground'>
        {medications.map(med => (
          <li key={med.patient_medication_id}>
            {med.display_name || 'Medication'} — obsolete in FDB
            {med.replacement_desc
              ? `, replaced by ${med.replacement_desc}`
              : ''}
            ; verify with pharmacy.
          </li>
        ))}
      </ul>
    </div>
  );
};

const ScreenResults: React.FC<{ result: InteractionScreen }> = ({ result }) => {
  const ddi = result.ddi ?? [];
  const da = result.da ?? [];
  const groups = SEVERITY_ORDER.map(code => ({
    code,
    label: SEVERITY_LABELS[code],
    findings: ddi.filter(f => severityBucket(f) === code),
  })).filter(group => group.findings.length > 0);

  const noFindings = ddi.length === 0 && da.length === 0;
  // The green all-clear may only speak for what was actually screened; when
  // items were excluded, the reassurance must carry that caveat with it.
  const hasExclusions =
    (result.not_screened ?? []).length > 0 ||
    (result.not_screened_allergies ?? []).length > 0;

  return (
    <div className='space-y-3'>
      {groups.map(group => (
        <div key={group.code} className='space-y-1'>
          <div className='flex items-center gap-1.5'>
            <Badge className={`text-xs ${SEVERITY_BADGE_CLASSES[group.code]}`}>
              {group.label}
            </Badge>
            <span className='text-xs text-muted-foreground'>
              ({group.findings.length})
            </span>
          </div>
          <ul className='space-y-0.5 text-sm'>
            {group.findings.map((finding, index) => (
              <li key={finding.interaction_id ?? `${group.code}-${index}`}>
                {finding.screen_message ||
                  finding.interaction_desc ||
                  'Interaction identified.'}
              </li>
            ))}
          </ul>
        </div>
      ))}

      {da.length > 0 && (
        <div className='space-y-1'>
          <div className='flex items-center gap-1.5'>
            <Badge className={`text-xs ${ALLERGY_BADGE_CLASSES}`}>
              Allergy
            </Badge>
            <span className='text-xs text-muted-foreground'>({da.length})</span>
          </div>
          <ul className='space-y-0.5 text-sm'>
            {da.map((finding, index) => (
              <li key={finding.allergen_id ?? `da-${index}`}>
                {finding.screen_message ||
                  finding.allergen_desc ||
                  'Allergy interaction identified.'}
              </li>
            ))}
          </ul>
        </div>
      )}

      {da.length === 0 && result.no_known_allergies && (
        <p className='text-xs text-muted-foreground'>
          No known allergies reported.
        </p>
      )}

      {noFindings &&
        (hasExclusions ? (
          <p className='text-sm text-muted-foreground'>
            No interactions found among the screened medications.
          </p>
        ) : (
          <div className='flex items-center gap-2 text-sm text-muted-foreground'>
            <ShieldCheck className='h-4 w-4 text-green-600' />
            No interactions identified.
          </div>
        ))}

      <ObsoleteMedicationsList
        medications={result.obsolete_medications ?? []}
      />

      <NotScreenedList
        medications={result.not_screened ?? []}
        allergies={result.not_screened_allergies ?? []}
      />
    </div>
  );
};

const InteractionScreenPanel: React.FC<InteractionScreenPanelProps> = ({
  patientId,
  acknowledged,
  onAcknowledgedChange,
  forceUnavailable = false,
}) => {
  const [status, setStatus] = useState<ScreenStatus>('idle');
  const [result, setResult] = useState<InteractionScreen | null>(null);

  const unavailable =
    forceUnavailable || status === 'error' || result?.unavailable === true;

  const handleScreen = async () => {
    setStatus('loading');
    try {
      const response = await screenMedicationInteractions({
        path: { patient_id: patientId },
      });
      if (response.error || !response.data) {
        console.error('Failed to screen interactions:', response.error);
        setResult(null);
        setStatus('error');
        return;
      }
      setResult(response.data);
      setStatus('ready');
    } catch (error) {
      console.error('Failed to screen interactions:', error);
      setResult(null);
      setStatus('error');
    }
  };

  return (
    <div className='border-t pt-4 space-y-3'>
      <div className='flex items-center justify-between gap-3'>
        <p className='text-sm text-muted-foreground'>
          Check for drug interactions before signing.
        </p>
        <Button
          type='button'
          variant='secondary'
          size='sm'
          onClick={handleScreen}
          disabled={status === 'loading'}
        >
          {status === 'loading' && <Loader2 className='animate-spin' />}
          {status === 'ready' || status === 'error'
            ? 'Re-screen'
            : 'Screen Interactions'}
        </Button>
      </div>

      {unavailable ? (
        <div className='space-y-2'>
          <div className='flex items-start gap-2 text-sm text-amber-700'>
            <AlertTriangle className='mt-0.5 h-4 w-4 flex-shrink-0' />
            <span>
              Interaction screening is unavailable. You can sign after
              acknowledging.
            </span>
          </div>
          <div className='flex items-start gap-2'>
            <Checkbox
              id='screening-unavailable-ack'
              checkboxSize='sm'
              checked={acknowledged}
              onCheckedChange={value => onAcknowledgedChange(value === true)}
            />
            <label
              htmlFor='screening-unavailable-ack'
              className='text-sm leading-snug cursor-pointer select-none'
            >
              Interaction screening was unavailable — I acknowledge and will
              review manually.
            </label>
          </div>
        </div>
      ) : status === 'ready' && result ? (
        <ScreenResults result={result} />
      ) : null}
    </div>
  );
};

export default InteractionScreenPanel;
